import type { Styles } from '@adornis/ass/style.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeInstitutionMailTemplateByID } from '@adornis/buchungsheld-db/api/mail-templates.js';
import { isDefined } from '@adornis/bh-util/util.js';
import { InstitutionMailTemplate, MailTemplateType } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { globalMailHint } from '@adornis/buchungsheld-db/mails/mail-template-catalog.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { XMailsTemplateEditor } from '@adornis/mails/client/x-mails-template-editor-legacy.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import '@adornis/popover/x-dropdown-selection.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { map, shareReplay } from 'rxjs';
import './tr-mail-editor.js';

/**
 * @element tr-mails-template-editor
 */
@customElement('tr-mails-template-editor')
export class TrMailsTemplateEditor extends XMailsTemplateEditor {
  @property({ type: Boolean }) overriding?: boolean;

  protected _getSymboleStyles(isActive: boolean): Styles {
    return {
      color: isActive ? this.colors.accent.shade(500) : this.colors.light.shade(500),
    };
  }

  private _renderTempDropDown(curTemp: MailTemplate, inPopup = false) {
    const latestValue = subscribeInstitutionMailTemplateByID(curTemp._id)({
      emailNotification: 1,
      whatsappNotification: 1,
    }).pipe(shareReplay(1));
    return html`
      <tr-flex
        template-dropdown-item
        horizontal
        center
        w100
        flex
        ${css({
          padding: `${this.spacing.sm} ${inPopup ? `CALC(${this.spacing.md} - 2px)` : '0'} ${this.spacing.sm} ${
            this.spacing.md
          }`,
        })}
      >
        <tr-flex center flex>
          <tr-text> ${curTemp.name} </tr-text>
        </tr-flex>

        <tr-flex targets space="sm" horizontal center crossaxis-center>
          ${obs(
            latestValue.pipe(
              map(
                template => html`
                  <tr-icon small ${css(this._getSymboleStyles(template?.emailNotification ?? false))}
                    >alternate_email</tr-icon
                  >
                  <tr-icon small outlined ${css(this._getSymboleStyles(template?.whatsappNotification ?? false))}
                    >phone_iphone</tr-icon
                  >
                `,
              ),
            ),
          )}
        </tr-flex>
      </tr-flex>
    `;
  }

  protected override _templateSelection() {
    console.log('this mailtemp', this.mailTemplates);
    const mailTemplates = Object.values(this.mailTemplates)
      .map(templates => templates.at(0))
      .filter(isDefined)
      .map(template => {
        // TODO: implement scope properly
        template.scope = 'default';
        return template;
      });

    if (mailTemplates.length > 1)
      return xComponents(html`
        <tr-dropdown-selection
          placeholder="Template auswählen"
          .value=${null}
          .selectables=${mailTemplates}
          .renderString=${(template: MailTemplate) => template.name}
          .renderSelected=${(curItem: MailTemplate) => this._renderTempDropDown(curItem)}
          .renderItem=${(curItem: MailTemplate) => this._renderTempDropDown(curItem, true)}
          @value-picked=${(e: ValueEvent<MailTemplate>) => {
            // TODO: implement scope properly
            this.selectedMailType = e.detail.value.emailType;
            this.selectedMailScope = 'default';
            this.requestUpdate();
          }}
          .guardFunction=${(item: MailTemplate) => {
            const template = item as InstitutionMailTemplate;
            return [template.whatsappNotification, template.emailNotification].join();
          }}
        ></tr-dropdown-selection>
        <x-hr></x-hr>
      `);
    return nothing;
  }

  protected override editSection(selectedTemplate: MailTemplate) {
    console.log({ selectedTemplate, foo: 32 });
    return html`
      <tr-flex space="md">
        <tr-flex horizontal space="md" flex>
          <tr-flex
            ${css({ background: '#fff', borderRadius: '5px', padding: this.spacing.lg, marginBottom: this.spacing.md })}
            space="md"
            flex
          >
            <tr-text bold> Beschreibung: </tr-text>
            <tr-text> ${globalMailHint(selectedTemplate.emailType as any as MailTemplateType)} </tr-text>
          </tr-flex>

          ${this._notificationTypeSelection()}
        </tr-flex>
        <tr-mail-editor
          .value=${selectedTemplate}
          ?clearable=${this.clearable}
          ?overriding=${this.overriding}
          attachments
        ></tr-mail-editor>
      </tr-flex>
    `;
  }

  protected _notificationTypeSelection() {
    const selectedMailTemplate = this._getSelectedTemplate() as InstitutionMailTemplate | null;
    if (!selectedMailTemplate) {
      console.warn('No selected mail template found');
      return nothing;
    }

    return html`
      <tr-flex
        ${css({
          background: '#fff',
          borderRadius: '5px',
          padding: this.spacing.lg,
          paddingRight: this.spacing.xl,
          marginBottom: this.spacing.md,
        })}
        space="md"
      >
        <tr-text bold> Empfangs-Dienste: </tr-text>
        <tr-flex space="sm">
          <tr-flex horizontal space="sm" crossaxis-center>
            ${
              [MailTemplateType.FORGOT_PASSWORD, MailTemplateType.OTP].includes(
                selectedMailTemplate.emailType as any as MailTemplateType,
              )
                ? html`
                    <tr-checkbox
                      disabled
                      .value=${true}
                      enableIcon="notifications"
                      disableIcon="notifications_off"
                    ></tr-checkbox>
                  `
                : html`
                    <tr-checkbox
                      enableIcon="notifications"
                      disableIcon="notifications_off"
                      .value=${
                        selectedMailTemplate?.emailNotification === undefined
                          ? true
                          : selectedMailTemplate?.emailNotification
                      }
                      @value-picked=${async (e: ValueEvent<boolean>) => {
                        if (!selectedMailTemplate) return;
                        selectedMailTemplate.emailNotification = e.detail.value;
                        if (!e.detail.value) selectedMailTemplate.whatsappNotification = false;
                        await selectedMailTemplate.save();
                      }}
                      boolean-only
                    ></tr-checkbox>
                  `
            }

            <tr-flex horizontal space="xxs" crossaxis-center>
              <tr-icon>alternate_email</tr-icon>
              <tr-text>E-Mail</tr-text>
            </tr-flex>
          </tr-flex>

          <tr-flex horizontal space="sm" crossaxis-center>
            ${
              [MailTemplateType.FREE_FORM].includes(selectedMailTemplate?.emailType as any as MailTemplateType)
                ? html` <tr-checkbox .disabled=${true} .value=${false} boolean-only></tr-checkbox> `
                : html`<tr-checkbox
                    .disabled=${!selectedMailTemplate?.emailNotification}
                    .value=${selectedMailTemplate?.whatsappNotification ?? false}
                    @value-picked=${async (e: ValueEvent<boolean>) => {
                      if (!selectedMailTemplate) return;
                      if (!selectedMailTemplate.emailNotification && e.detail.value) return;
                      selectedMailTemplate.whatsappNotification = e.detail.value;
                      await selectedMailTemplate.save();
                    }}
                    boolean-only
                  ></tr-checkbox>`
            }

            <tr-flex horizontal space="xxs" crossaxis-center>
              <tr-icon outlined>phone_iphone</tr-icon>
              <tr-text>WhatsApp</tr-text>
            </tr-flex>
          </tr-flex>
        </tr-flex>
      </tr-flex>
    `;
  }
}
